// Screening-session screens.
//
// Kept OUT of canonicalContent.generated / instruction_screens.md on purpose: those
// are the frozen S&T-derived screens under the 2026-07-20 fidelity rule. These are
// new text for the batched screening call, so they live separately rather than
// editing the canonical module.

// Shown first — the participant has agreed to "a study" and needs the shape of the
// whole commitment before they start, since parts 2 and 3 happen on their own time.
export const SCREENING_OVERVIEW = `
**Welcome, and thank you for taking part.**

This study runs in **three parts**. Today is the first, and it's the only one we do together.

**Part 1 — today, about 30 minutes (this call).**
A short Python exercise so we can see how you work, followed by a few quick questions.
No AI tools and no outside resources for this one — just you and the editor.

**Part 2 — on your own, about 80–90 minutes.**
You'll learn a Python library called Trio by working through tasks. An AI tutor is
available for part of it. You'll get a personal link by email when this opens.

**Part 3 — 6 to 8 days later, about 20 minutes.**
One short programming task and a couple of wrap-up questions. You'll book this slot
yourself at the end of Part 2, and the completion bonus is attached to finishing it.

Everything runs in this browser. You can use the same link each time — it's personal
to you, so please don't share it.

☐ **I understand the study has three parts and I'm able to take part in all three.**
`.trim()
// The ☐ line is load-bearing: InstructionStep pulls the acknowledgment label out of the
// screen text, and without one an `ackRequired` step falls back to M0's hardcoded
// "My screen share and camera are on" — wrong wording for this screen. Making the
// commitment explicit up front also costs less than losing someone at part 3.

// Shown after the warm-up task, before they leave the call.
export const SCREENING_NEXT = `
**That's Part 1 done — thank you.**

**What happens next:**

- We'll review today's exercise shortly after this call.
- If you continue, you'll get an **email with your personal link** for Part 2, along
  with roughly how long to set aside. Use the same link you used today.
- **Part 2 takes about 80–90 minutes and runs in timed blocks**, so start it when you
  have an uninterrupted stretch — you can't pause a block once it starts.
- At the end of Part 2 you'll pick your Part 3 slot, 6 to 8 days out.

**A note on the tasks ahead:** some blocks give you an AI tutor and some are
deliberately on your own. The instructions will say clearly which is which, and the
parts done on your own are the ones the study is actually measuring — so please play
those straight, even though nobody is watching.

You can close this window now. Keep an eye on your email.
`.trim()

// The warm-up task doc mixes participant text with the internal scoring rubric and the
// eligibility gate. Only the "Participant-facing statement" section may ever be shown.
//
// That section still ends with a file-manifest line naming `warmup_solution.py` as the
// "proctor reference" — written for whoever assembles the study, not for the person
// taking it. It is dropped here: it advertises that a solution file exists, and the
// filenames are wrong on the platform anyway (the editor is a single `main.py`).
export function warmupParticipantMd (md) {
  const HEADING = '## Participant-facing statement'
  const start = md.indexOf(HEADING)
  if (start === -1) return md
  const body = md.slice(start + HEADING.length)
  const end = body.indexOf('\n## ')
  return (end === -1 ? body : body.slice(0, end))
    .split('\n')
    .filter(l => !/^\s*(\*+\s*)?Files:/i.test(l))
    .join('\n')
    .trim()
}
